<script setup lang="ts">
import type { ProductFragment } from '#graphql-operations'
import data from '~~/reviews_fdhjs32SDfd.json'

const props = defineProps<{
  productData: ProductFragment
}>()

const { t } = useI18n()

const lastDigit = Number.parseInt(props.productData.id.slice(-1))
const twoLastDigits = Number.parseInt(props.productData.id.slice(-2))
const customersRecommends = 100 - Math.floor(lastDigit / 2)
const customersBought = Math.floor(twoLastDigits + 4 * lastDigit)

const disputeRate = (lastDigit % 2 === 0) ? 1 + twoLastDigits / 100 : (twoLastDigits * 2) / 100

const formattedDisputeRate = disputeRate.toFixed(2)

const reviews = ref<typeof data>([])
const ratingCounts = ref({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 })
const ratingPercentages = ref({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 })
const averageScore = ref(0)
const totalReviewsShown = ref(0)
const reviewModalOpen = ref(false)

async function fetchReviews() {
  try {
    const numOfReviews = Math.floor(Math.random() * 13) + 9

    const shuffledReviews = data.sort(() => 0.5 - Math.random())
    reviews.value = shuffledReviews.slice(0, numOfReviews)
    totalReviewsShown.value = reviews.value.length

    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
    let totalScore = 0

    reviews.value.forEach(({ rating }) => {
      if (counts[rating as keyof typeof counts] !== undefined) {
        counts[rating as keyof typeof counts]++
        totalScore += rating
      }
    })

    ratingCounts.value = counts
    const totalReviews = reviews.value.length
    for (const rating in counts) {
      ratingPercentages.value[rating] = ((counts[rating] / totalReviews) * 100).toFixed(2)
    }

    averageScore.value = (totalScore / totalReviews).toFixed(1)
  }
  catch (error) {
    console.error('Failed to fetch reviews:', error)
  }
}

onMounted(fetchReviews)
</script>

<template v-if="reviews">
  <div class="">
    <div class="w-full rounded-lg bg-gray-50 p-6">
      <div class="flex flex-col justify-center gap-y-4 md:flex-row md:flex-wrap md:items-center md:justify-between">
        <div class="flex flex-col items-center md:mr-8">
          <span class="text-4xl font-bold">{{ averageScore }}</span>
          <div class="mt-1 flex items-center">
            <Rating :rating="averageScore" class="-ml0.5" />
          </div>
          <span class="mt-1 text-sm text-gray-500">{{ t('review.rated_by_x_customers', [totalReviewsShown]) }}</span>
          <NButton
            id="reviewModal"
            n="borderless sm"
            class="mt3 rounded bg-green-600 px-4 py-2 text-sm text-white font-bold hover:text-white"
            @click="reviewModalOpen = true"
          >
            {{ t('review.write_review') }}
          </NButton>
        </div>

        <div class="w-full md:w-1/3">
          <NProgressBar :percentage-width="ratingPercentages[5]" star-count="5" :review-count="ratingCounts[5]" />
          <NProgressBar :percentage-width="ratingPercentages[4]" star-count="4" :review-count="ratingCounts[4]" />
          <NProgressBar :percentage-width="ratingPercentages[3]" star-count="3" :review-count="ratingCounts[3]" />
          <NProgressBar :percentage-width="ratingPercentages[2]" star-count="2" :review-count="ratingCounts[2]" />
          <NProgressBar :percentage-width="ratingPercentages[1]" star-count="1" :review-count="ratingCounts[1]" />
        </div>

        <div class="grid grid-cols-2 mx-8 gap-8">
          <div class="flex flex-col items-center">
            <div class="flex items-center">
              <Icon name="i-hugeicons:shopping-cart-01" class="mr-2 text-2xl text-gray-500" />
              <span class="font-bold">{{ customersBought }} +</span>
            </div>
            <span class="text-sm text-gray-500">{{ t('review.stats.customers_bughout') }}</span>
            <div class="mt-4 flex items-center">
              <Icon name="i-hugeicons:thumbs-up" class="mr-2 text-2xl text-gray-500" />
              <span class="font-bold">{{ totalReviewsShown }}</span>
            </div>
            <span class="text-sm text-gray-500">{{ t('review.stats.customers_rated') }}</span>
          </div>

          <div class="flex flex-col items-center">
            <div class="flex items-center">
              <Icon name="i-hugeicons:star-face" class="mr-2 text-2xl text-gray-500" />
              <span class="font-bold">{{ customersRecommends }} %</span>
            </div>
            <span class="text-sm text-gray-500">{{ t('review.stats.customers_recommends') }}</span>
            <div class="mt-4 flex items-center">
              <Icon name="i-hugeicons:bubble-chat-done" class="mr-2 text-2xl text-green-500" />
              <span class="text-green-500 font-bold">{{ formattedDisputeRate }} %</span>
            </div>
            <div class="flex items-center">
              <span class="text-sm text-green-500">{{ t('review.stats.low_dispute_rate') }}</span>
              <i class="fas fa-info-circle ml-1 text-gray-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-4 max-h-520px flex flex-col gap-y-4 overflow-y-scroll px1 shadow-sm">
      <Review v-for="(review, index) in reviews" :key="index" :review-data="review" />
    </div>
    <div>
      <NModal :open="reviewModalOpen" @close="reviewModalOpen = false">
        <HDialogPanel
          class="max-w-2xl w-full transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
        >
          <div class="absolute right-0 top-0 pr4 pt4">
            <div class="absolute right-0 top-0 pr4 pt4">
              <NButton
                id="close-size-chat-modal"
                n="lg borderless"
                icon="i-mingcute:close-line"
                class="p-0.25em text-context children:m-0"
                @click="reviewModalOpen = false"
              />
            </div>
          </div>
          <div>
            <div class="p5">
              <h2 class="text-center text-2xl text-xl text-gray-900 font-bold tracking-tight uppercase">
                {{ t('review.write_review') }}
              </h2>
              <div class="mb-4 mt-6 flex items-center justify-between">
                <form class="w-full">
                  <div class="w-full flex flex-row gap-x-2">
                    <div class="w-1/2">
                      <label for="fm" class="block text-sm text-gray-900 font-medium leading-6">{{
                        t('review.new_review.name')
                      }}</label>
                      <div class="relative mt-2 rounded-md shadow-sm">
                        <input
                          id="fm" type="text" name="fm"
                          class="block w-full border-0 rounded-md py-1.5 pr-10 text-gray-900 ring-1 ring-gray-300 ring-inset sm:text-sm placeholder:text-gray-400 sm:leading-6 focus:ring-2 focus:ring-indigo-600 focus:ring-inset"
                        >
                        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3" />
                      </div>
                    </div>
                    <div class="w-1/2">
                      <label for="lm" class="block text-sm text-gray-900 font-medium leading-6">{{
                        t('review.new_review.name')
                      }}</label>
                      <div class="relative mt-2 rounded-md shadow-sm">
                        <input
                          id="lm" type="text" name="lm"
                          class="block w-full border-0 rounded-md py-1.5 pr-10 text-gray-900 ring-1 ring-gray-300 ring-inset sm:text-sm placeholder:text-gray-400 sm:leading-6 focus:ring-2 focus:ring-indigo-600 focus:ring-inset"
                        >
                        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3" />
                      </div>
                    </div>
                  </div>
                  <div class="w-full flex flex-row gap-x-2">
                    <div class="mt2 w-full">
                      <label for="location" class="block text-sm text-gray-900 font-medium leading-6">
                        {{ t('review.new_review.yours_rating') }}</label>
                      <select
                        id="location" name="location"
                        class="mt-2 block w-full w-full border-0 rounded-md py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-gray-300 ring-inset sm:text-sm sm:leading-6 focus:ring-2 focus:ring-indigo-600"
                      >
                        <option disabled selected>
                          {{ t('review.new_review.yours_rating') }}
                        </option>
                        <option>
                          ★★★★★
                        </option>
                        <option>
                          ★★★★
                        </option>
                        <option>
                          ★★★
                        </option>
                        <option>
                          ★★
                        </option>
                        <option>
                          ★
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="w-full flex flex-row gap-x-2">
                    <div class="w-1/2">
                      <div class="mt-2">
                        <div>
                          <label for="comment" class="block text-sm text-gray-900 font-medium leading-6">Výhody</label>
                          <div class="mt-2">
                            <textarea
                              id="comment" rows="4" name="comment"
                              class="block w-full border-0 rounded-md py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset sm:text-sm placeholder:text-gray-400 sm:leading-6 focus:ring-2 focus:ring-indigo-600 focus:ring-inset"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="w-1/2">
                      <div class="mt-2">
                        <div>
                          <label
                            for="comment"
                            class="block text-sm text-gray-900 font-medium leading-6"
                          >Nevýhody</label>
                          <div class="mt-2">
                            <textarea
                              id="comment" rows="4" name="comment"
                              class="block w-full border-0 rounded-md py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset sm:text-sm placeholder:text-gray-400 sm:leading-6 focus:ring-2 focus:ring-indigo-600 focus:ring-inset"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="mt5 w-full flex flex-row gap-x-2">
                    <div class="w-1/2">
                      <div>
                        <label for="image_input" class="block text-sm text-gray-900 font-medium leading-6">
                          {{ t('review.new_review.product_photos') }}
                        </label>
                        <div class="mt-2">
                          <input type="file">
                        </div>
                      </div>
                    </div>
                    <div class="w-1/2 flex items-center justify-center">
                      <NButton
                        id="close-size-chat-modal"
                        n="lg borderless"
                        class="mt3 rounded bg-green-600 px-4 py-4 text-xs text-white font-bold hover:text-white"
                        @click="alert('Odesláno ke schválení')"
                      >
                        {{ t('review.new_review.submit_review') }}
                      </NButton>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </HDialogPanel>
      </NModal>
    </div>
  </div>
</template>
